/* eslint-disable no-param-reassign */
import _ from 'underscore';
import pgrid from 'projection-grid';

import { merge } from '@bingads-webui-universal/primitive-utilities';

function visit(node, graph, permVisited, tempVisited, sortedList) {
  if (permVisited[node]) {
    return;
  }

  if (tempVisited[node]) {
    throw new Error(`Circular plugin dependency detected: ${node}`);
  }

  tempVisited[node] = true;
  _.each(graph[node], dep => visit(dep, graph, permVisited, tempVisited, sortedList));
  tempVisited[node] = false;
  permVisited[node] = true;
  sortedList.unshift(node);
}

function topoSort(graph) {
  const permVisited = {};
  const tempVisited = {};
  const notVisited = _.keys(graph);
  const sortedList = [];

  while (notVisited.length > 0) {
    const currNode = notVisited.pop();
    visit(currNode, graph, permVisited, tempVisited, sortedList);
  }

  return sortedList;
}

function generateDepGraph(map) {
  const graph = {};

  _.map(_.uniq(_.flatten(_.values(map))), (dep) => {
    graph[dep] = [];
  });

  _.each(map, (deps, name) => {
    _.each(deps, (dep) => {
      graph[dep].push(name);
    });
  });

  return graph;
}

function processDeps(map) {
  return _.mapObject(map, deps => _.without(deps, 'gridView', 'config', 'dataSource'));
}

function getOrderToInitializePlugin(nameToDepsMap) {
  const sortedList = topoSort(generateDepGraph(processDeps(nameToDepsMap)));

  return sortedList.concat(_.difference(_.keys(nameToDepsMap), sortedList));
}

function generatePluginNameMaps(plugins) {
  const nameToPluginRefMap = {};
  const nameToDepsMap = {};
  const softDependenciesMap = {};
  _.each(plugins, ({ plugin }) => plugin((name, deps) => {
    nameToDepsMap[name] = deps;
    nameToPluginRefMap[name] = plugin;
    if (_.isArray(plugin.softDependencies)) {
      softDependenciesMap[name] = plugin.softDependencies;
    }
  }));

  // softDependencies is not required dependency.
  // PluginA or PluginB doesn't depend each other. Grid can use the PluginA or PluginB directly.
  // But, if grid use them together, sometimes, we must use one firstly, use another one secondly.
  // An example is SegmentPlugin, and AggregatedRowPlugin, must use AggregatedRowPlugin fristly.
  _.each(softDependenciesMap, (softDependencies, name) => {
    _.each(softDependencies, (softDependency) => {
      if (_.has(nameToDepsMap, softDependency)) {
        nameToDepsMap[name].push(softDependency);
      }
    });
  });

  return {
    nameToPluginRefMap,
    nameToDepsMap,
  };
}

function addPluginConfigToGridConfig(gridConfig, plugins, preferenceConfig) {
  gridConfig.plugins = {};

  _.each(plugins, ({ plugin, config }) => {
    if (config) {
      plugin((name) => {
        if (name === 'performance-log') {
          gridConfig.plugins.performanceLog = config;
        } else {
          gridConfig.plugins[name] = config;
        }
      });
    }
  });

  // merge here as preferenceConfig contains modifications for both gridConfig and plugin config
  merge(gridConfig, preferenceConfig);

  return gridConfig;
}

export function pgridFactory({ plugins = [], gridConfig, preferenceConfig }, grid = pgrid) {
  const { nameToPluginRefMap, nameToDepsMap } = generatePluginNameMaps(plugins);
  // add customDepsMap to allow customize for the depsMap to solve below scenario
  // Plugin A depend on Plugin B when Plugin B exist.
  // In this scenario, we couldn't add plugin B in plugin A's dependency directly
  // as plugin B is not required by plugin A
  if (gridConfig && _.isFunction(gridConfig.customDepsMap)) {
    gridConfig.customDepsMap(nameToDepsMap);
  }
  const sortedOrder = getOrderToInitializePlugin(nameToDepsMap);

  let gridFactory = grid.factory({ vnext: true });

  _.each(sortedOrder, (pluginName) => {
    gridFactory = gridFactory.use(nameToPluginRefMap[pluginName]);
  });

  return gridFactory.create(addPluginConfigToGridConfig(gridConfig, plugins, preferenceConfig));
}
